import {makeAutoObservable, observable, runInAction} from "mobx";
import {injectable, inject} from "inversify";
import {Bindings} from "data/constants/bindings";
import type {IRosterItem} from "data/types/roster";
import type {IRankingsApiProvider} from "data/providers/api/rankings.api.provider";
import type {IRankingsProfile} from "data/types/rankings";
import {IGameBar} from "data/stores/game_bar/game_bar.store";

export interface IRankingsProfilePayload {
	contestId: number;
	userId: number;
}

export interface IUserRosterStore {
	get userRoster(): IRosterItem | undefined;
	get displayName(): IRankingsProfile["user"]["displayName"] | undefined;
	get userGameBar(): IGameBar | undefined;
	get userId(): number;
	get contestIds(): number[];
	fetchUserRoster(contestId: number, userId: number): Promise<void>;
	setContestId(contestId: number): void;

	clear: () => void;
}

@injectable()
export class UserRosterStore implements IUserRosterStore {
	@observable _contestId: number = 0;
	@observable _userId: number = 0;
	@observable private _rankingsProfile?: IRankingsProfile;

	constructor(
		@inject(Bindings.RankingsApiProvider) private _rankingsApiProvider: IRankingsApiProvider
	) {
		makeAutoObservable(this);
	}

	get userRoster(): IRosterItem | undefined {
		return this.rankingsProfile?.roster;
	}

	get userGameBar(): IGameBar | undefined {
		return this.rankingsProfile?.gamebar;
	}

	get userId(): number {
		return this._userId;
	}

	get contestIds(): number[] {
		return this.rankingsProfile?.contestIds || [];
	}

	get rankingsProfile() {
		return this._rankingsProfile;
	}

	get displayName(): IRankingsProfile["user"]["displayName"] | undefined {
		return this._rankingsProfile?.user?.displayName;
	}

	async fetchUserRoster(contestId: number, userId: number) {
		this._userId = userId;
		this._contestId = contestId;

		const response = await this._rankingsApiProvider.profile({
			contestId,
			userId,
		});
		const responseRoster = response.data.success;

		runInAction(() => {
			this._rankingsProfile = responseRoster;
		});
	}

	clear = () => {
		this._userId = 0;
		this._rankingsProfile = undefined;
	};

	setContestId(contestId: number) {
		this._contestId = contestId;
	}
}
